// docs/plan/v0.4.0-parts-solution-defect-lot/plan.md §5.1 + §5.2
// 부품 / LOT / 영향권 / RobotPart read + write endpoints.

import type {
  DefectStatus,
  PartCategory,
  PartCatalogSummaryDto,
  PartLotSummaryDto,
  PartLotImpactDto,
  RobotPartDto,
} from '@bhops/shared';
import { apiClient } from './client';

export interface ListCatalogQuery {
  category?: PartCategory;
  active?: boolean;
  search?: string;
  // v0.5.2 — 부족 catalog 만.
  lowStockOnly?: boolean;
}

export interface ListLotsQuery {
  defectStatus?: DefectStatus;
  partCatalogId?: string;
  search?: string;
}

export const partsApi = {
  async listCatalog(query: ListCatalogQuery = {}): Promise<PartCatalogSummaryDto[]> {
    const { data } = await apiClient.get<PartCatalogSummaryDto[]>('/parts/catalog', {
      params: query,
    });
    return data;
  },
  async getCatalog(id: string): Promise<PartCatalogSummaryDto> {
    const { data } = await apiClient.get<PartCatalogSummaryDto>(`/parts/catalog/${id}`);
    return data;
  },
  async listLots(query: ListLotsQuery = {}): Promise<PartLotSummaryDto[]> {
    const { data } = await apiClient.get<PartLotSummaryDto[]>('/parts/lots', {
      params: query,
    });
    return data;
  },
  async getLot(id: string): Promise<PartLotSummaryDto> {
    const { data } = await apiClient.get<PartLotSummaryDto>(`/parts/lots/${id}`);
    return data;
  },
  async getLotImpact(id: string): Promise<PartLotImpactDto> {
    const { data } = await apiClient.get<PartLotImpactDto>(`/parts/lots/${id}/impact`);
    return data;
  },
  async listForRobot(robotId: string): Promise<RobotPartDto[]> {
    const { data } = await apiClient.get<RobotPartDto[]>(`/robots/${robotId}/parts`);
    return data;
  },

  // ── Write (v0.4.0-beta) ─────────────────────────────────

  async createCatalog(body: {
    sku: string;
    displayName: string;
    category: PartCategory;
    modelCompatibilityCodes?: string[];
    unitPriceKrw?: number;
    warrantyMonths?: number;
    active?: boolean;
    // v0.5.2 — 재고 자동화.
    minStockLevel?: number;
    reorderQty?: number;
    defaultSupplierId?: string;
  }): Promise<PartCatalogSummaryDto> {
    const { data } = await apiClient.post<PartCatalogSummaryDto>('/parts/catalog', body);
    return data;
  },

  async createLot(body: {
    partCatalogId: string;
    lotCode: string;
    // v0.4.0 legacy — free-text 공급사명. v0.5.0 부터 supplierId 권장.
    supplierName?: string;
    // v0.5.0 — Supplier FK. backend dto/create-part-lot.dto.ts:33 와 1:1.
    // PartLotCreateModal 이 두 모드 (FK / free-text) 를 토글 노출.
    supplierId?: string;
    manufacturedAt?: string;
    receivedQty: number;
  }): Promise<PartLotSummaryDto> {
    const { data } = await apiClient.post<PartLotSummaryDto>('/parts/lots', body);
    return data;
  },

  async updateLotDefectStatus(
    id: string,
    body: { defectStatus: DefectStatus; reason?: string },
  ): Promise<PartLotSummaryDto> {
    const { data } = await apiClient.patch<PartLotSummaryDto>(
      `/parts/lots/${id}/defect-status`,
      body,
    );
    return data;
  },

  async recall(
    id: string,
    body: { reason?: string },
  ): Promise<{
    lotId: string;
    defectStatus: 'CONFIRMED';
    createdServiceOrders: Array<{ id: string; humanId: string; robotId: string }>;
  }> {
    const { data } = await apiClient.post(`/parts/lots/${id}/recall`, body);
    return data;
  },

  async installPart(
    robotId: string,
    body: {
      partCatalogId: string;
      partLotId?: string;
      slot: string;
      serialNo?: string;
      installedAt?: string;
      installedByVisitId?: string;
    },
  ): Promise<RobotPartDto> {
    const { data } = await apiClient.post<RobotPartDto>(`/robots/${robotId}/parts`, body);
    return data;
  },

  async removePart(
    id: string,
    body: {
      removalReason: string;
      removedAt?: string;
      removedByVisitId?: string;
      notes?: string;
    },
  ): Promise<RobotPartDto> {
    const { data } = await apiClient.patch<RobotPartDto>(`/robot-parts/${id}/remove`, body);
    return data;
  },
};
